import { ProjectHubResponseProjectPlanId } from '@platform/client/schemas';
import { AvatarNew, Button, Icon, IconProps, Tooltip } from '@platform/components';
import { Focusable } from 'react-aria';
import { Link } from 'wouter';

export type SideBarItemProps =
  | (MaterialCardProps & { type: 'material' })
  | (ContactCardProps & { type: 'contact' });

export const SideBar = ({ title, items }: { title: string; items: SideBarItemProps[] }) => {
  return (
    <div className='w-full md:w-[253px] px-3 py-4 border border-gray-100 rounded-xl bg-white'>
      <div className='text-xl font-medium 0 mb-4'>{title}</div>
      <div className='flex flex-col gap-4'>
        {items.map((item) =>
          item.type === 'contact' ?
            <ContactCard
              key={item.email}
              firstname={item.firstname}
              lastname={item.lastname}
              email={item.email}
              avatarUrl={item.avatarUrl}
              profileUrl={item.profileUrl}
            />
            : <MaterialSideBarItem
              key={item.title}
              title={item.title}
              icon={item.icon}
              url={item.url}
              description={item.description}
            />,
        )}
      </div>
    </div>
  );
};

export const ProjectPlanCard = ({ projectPlanId }: { projectPlanId: ProjectHubResponseProjectPlanId }) => {
  return (
    <div className='px-3 py-4 border border-gray-100 rounded-xl bg-white'>
      <div className='flex items-center gap-2 mb-2'>
        <div className='flex items-center justify-center bg-success-100 h-8 w-8 rounded-full'>
          <Icon type='file-check-02' size='24' additionalClassNames='text-success-500 inline' />
        </div>
        <h3 className='text-xl text-gray-700 inline font-medium'>Project Plan</h3>
      </div>
      {projectPlanId ?
        <div>
          <p className='text-xs text-gray-500 mb-4'>This is your current project plan.</p>
          <Button
            variant='primary'
            size='sm'
            href={`/project-plan/${projectPlanId}`}
          >
            See Project Plan
          </Button>
        </div>
        : <div className='text-xs text-gray-500'>Your plan will appear here once you reach the Planning step</div>}
    </div>
  )
}

type MaterialCardProps = {
  title: string;
  icon: IconProps['type'];
  url: string;
  description: string;
};

const MaterialSideBarItem = (props: MaterialCardProps) => {
  return (
    <div className='flex gap-2 w-full md:w-[229px] border border-gray-100 rounded-xl py-4 px-3.5 bg-gray-50'>
      <AvatarNew size='md' fallbackIcon={props.icon} />
      <div>
        <div className='text-sm font-semibold text-brand-100'>{props.title}</div>
        <div className='text-xs text-gray-500'>{props.description}</div>
      </div>
    </div>
  );
};

type ContactCardProps = {
  firstname: string;
  lastname: string;
  email: string;
  avatarUrl?: string;
  profileUrl?: string;
};

export const ContactCard = (props: ContactCardProps) => {
  const displayName = formatDisplayName(props.firstname, props.lastname);
  return (
    <div className='flex gap-2 w-full md:w-[229px] border border-gray-100 rounded-xl py-4 px-3.5 bg-gray-50'>
      <AvatarNew size='md' url={props.avatarUrl} fallbackIcon='user-01' />
      <div>
        {/* Tooltip requires focusable component for mouse over */}
        <Tooltip supportingText={`${props.firstname} ${props.lastname}`}>
          <Focusable>
            <h4 className='text-base font-medium mb-1'>
              {props.profileUrl ?
                <Link className='text-brand-100' to={props.profileUrl}>
                  {displayName}
                </Link>
                : displayName}
            </h4>
          </Focusable>
        </Tooltip>
        <Button
          variant='link-color'
          size='sm'
          additionalClassnames='w-fit -m-0.5'
          tailIcon='mail-01'
          href={`mailto:${props.email}`}
        >
          Email
        </Button>
      </div>
    </div>
  );
};

const formatDisplayName = (firstname: string, lastname: string) => {
  if (firstname.length + lastname.length + 1 <= 20) {
    return `${firstname} ${lastname}`;
  } else if (`${firstname} ${lastname.charAt(0)}.`.length <= 20) {
    return `${firstname} ${lastname.charAt(0)}.`;
  } else if (firstname.length <= 20) {
    return firstname;
  } else {
    return `${firstname.charAt(0)}.${lastname.charAt(0)}.`;
  }
};


